import React, { useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { Region } from '../../../data/types/region-types';
import { formatCurrencyShort } from '../../../data/utils/money-utils';

// This component is read-only according to the allowed files, but I'm checking it
// to ensure we understand how region labels are created and displayed

interface RegionLabelsProps {
  map: mapboxgl.Map;
  regions: Region[];
  filteredRegionIds?: string[];
  markerRefs: mapboxgl.Marker[];
}

const RegionLabels: React.FC<RegionLabelsProps> = ({ map, regions, filteredRegionIds, markerRefs }) => {
  const [labelElements, setLabelElements] = useState<HTMLElement[]>([]);
  
  // Create and add the region labels to the map
  useEffect(() => {
    if (!map) return;
    
    // Clean up old markers
    markerRefs.forEach(marker => marker.remove());
    markerRefs.length = 0;
    
    // Create the new region labels
    const newLabels: HTMLElement[] = [];
    
    regions.forEach(region => {
      if (filteredRegionIds && !filteredRegionIds.includes(region.region_id)) {
        return;
      }
      
      // Create the region label element
      const regionLabel = document.createElement('div');
      regionLabel.className = 'region-label';
      regionLabel.textContent = region.english_name;
      
      // Offset the marker position slightly to avoid overlap with the region name
      const markerPosition: [number, number] = [
        region.coordinates[0] - 0.5,
        region.coordinates[1] + 0.5
      ];
      
      // Create and add the marker
      const marker = new mapboxgl.Marker({
        element: regionLabel,
        anchor: 'bottom'
      })
        .setLngLat(markerPosition)
        .addTo(map);
      
      // Store the marker in the refs array
      markerRefs.push(marker);
      newLabels.push(regionLabel);
    });
    
    setLabelElements(newLabels);
    
    // Clean up on component unmount
    return () => {
      markerRefs.forEach(marker => marker.remove());
      markerRefs.length = 0;
    };
  }, [map, regions, filteredRegionIds]);
  
  return null;
};

export default RegionLabels;
